import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useThemeChange } from "../../../../utils/themeChange";
import { useToastContext } from "../../../../modules/toast/context";
import { getBase64 } from "../../../../utils/encodeimage";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "../../../../hooks/api/useApi";
import useAuthStore from "../../../../store/authStore";

import {
  ART,
  LICENSE,
  LICENSE_TYPES,
  CATEGORIES,
} from "../../../../constants/api-constants";

import Loading from "../../../../components/components/Loading";
import SingleSelect from "../../../components/singleSelect";
import MultipleSelect from "../../../components/multipleSelect";
import ArtCard from "../../../components/artCard";

const { ADD_ART } = ART;
const { LIST_LICENSE_TYPES } = LICENSE_TYPES;
const { LIST_TERRITORIES, LIST_TERMS } = LICENSE;
const { LIST_PRODUCT_CATEGORIES } = CATEGORIES;

const defaultArtDetail = {
  name: "",
  description: "",
};

const CreateArt = () => {
  const themeChange = useThemeChange();
  const navigate = useNavigate();
  const { userData } = useAuthStore((state) => ({
    userData: state.userLog,
  }));
  const { post, getAll } = useApi();
  const { showError, showSuccess } = useToastContext();
  const [artDetail, setArtDetail] = useState(defaultArtDetail);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [image, setImage] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showFilteredOptions, setShowFilteredOptions] = useState(false);

  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowFilteredOptions(false);
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  const productCategory = useQuery({
    queryKey: ["product-categories", searchText],
    queryFn: () => getAll(LIST_PRODUCT_CATEGORIES, { name: searchText }),
  });

  const licenseTypes = useQuery({
    queryKey: ["license-types"],
    queryFn: () => getAll(LIST_LICENSE_TYPES),
  });

  if (licenseTypes.isSuccess) {
    var licenseType = licenseTypes?.data.map((l) => ({
      value: l.id,
      label: l.name,
    }));
  }

  const licenseTerritories = useQuery({
    queryKey: ["license-territories"],
    queryFn: () => getAll(LIST_TERRITORIES),
  });

  if (licenseTerritories.isSuccess) {
    var licenseTerritory = licenseTerritories?.data.map((t) => ({
      value: t.id,
      label: t.name,
    }));
  }

  const licenseTerms = useQuery({
    queryKey: ["license-terms"],
    queryFn: () => getAll(LIST_TERMS),
  });

  if (licenseTerms.isSuccess) {
    var licenseTerm = licenseTerms?.data.map((t) => ({
      value: t.id,
      label: t.year,
    }));
  }

  const createArt = useMutation({
    mutationFn: post,
    onSuccess: () => {
      showSuccess("Art Successfully Created.");
    },
    onError: () => {
      showError("Art Creation Unsuccessfull.");
    },
  });

  if (createArt.isSuccess) navigate(`/license/${createArt?.data?.id}`);

  const onChange = (e) => {
    const { name, value } = e.target;
    setArtDetail((prev) => ({ ...prev, [name]: value }));
  };

  const onTerritorySelect = (selectedTerritory) => {
    setSelectedTerritory(selectedTerritory);
    setArtDetail((prev) => ({
      ...prev,
      licenseTerritory: selectedTerritory?.map((t) => t.label),
    }));
  };

  const onTermSelect = (selectedTerm) => {
    setSelectedTerm(selectedTerm);
    setArtDetail((prev) => ({
      ...prev,
      licenseTerms: selectedTerm?.map((t) => t.label),
    }));
  };

  const onLicenseSelect = (selectedLicense) => {
    setSelectedLicense(selectedLicense);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setShowFilteredOptions(true);
  };

  const handleOptionClick = (category) => {
    setSelectedCategory(category);
    setSearchText(category.name);
    setShowFilteredOptions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!previewImage) return showError("Please upload image.");
    if (!selectedCategory) return showError("Please select product category.");
    if (!selectedTerm) return showError("Please select license term.");
    if (!selectedLicense) return showError("Please select license option.");
    if (!selectedTerritory)
      return showError("Please select license territory.");

    createArt.mutate({
      ...artDetail,
      name: selectedCategory?.name,
      imageUrl: previewImage,
      artistId: userData?.artist?.id,
      licenseTypes: selectedLicense?.label,
      access_token: userData?.access_token,
      route: ADD_ART,
    });
  };
  return (
    <div className={themeChange.add("section")}>
      <section className="jumbotron breadcumb no-bg">
        <div className="mainbreadcumb">
          <div className="container">
            <div className="row m-10-hor">
              <div className="col-12">
                <h1 className="text-center">List License Opportunity</h1>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="container">
        <div className="row">
          <div className="col-lg-7 offset-lg-1 mb-5">
            <form
              id="form-create-item"
              className="form-border"
              action=""
              onSubmit={handleSubmit}
            >
              <div className="field-set">
                <div className="row">
                  <div className="col-lg-9">
                    <h5>
                      Upload Sample Artwork
                      <span className="error-message-color">*</span>
                    </h5>

                    <div className="d-create-file">
                      {image && image.length > 0 ? (
                        <>
                          <img
                            id="close"
                            src={image}
                            alt=""
                            height={200}
                            width={200}
                          />
                          <div className="spacer-single" />
                        </>
                      ) : (
                        <>
                          <p key="{index}">PNG, JPG, or JPEG</p>
                        </>
                      )}
                      <div className="browse">
                        <input
                          type="button"
                          id="get_file"
                          className="btn-main"
                          value="Browse"
                        />
                        <input
                          id="upload_file"
                          type="file"
                          name="peviewImage"
                          onChange={(e) => {
                            const imgSize = e.target.files[0].size;
                            if (imgSize > 5 * 1024 * 1024) {
                              showError("Image size should be less than 5MB");
                              return;
                            }
                            setImage(URL.createObjectURL(e.target.files[0]));
                            getBase64(e.target.files[0], setPreviewImage);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="spacer-single"></div>
                <h5>
                  Product Category
                  <span className="error-message-color">*</span>
                </h5>
                <div className="search_bar" ref={searchInputRef}>
                  <input
                    value={searchText}
                    placeholder="Search product category..."
                    onChange={handleSearch}
                  />
                  {showFilteredOptions && (
                    <div className="border_top">
                      {productCategory?.data?.length
                        ? productCategory?.data?.map((category) => (
                            <div
                              key={category.id}
                              className="category_option"
                              onClick={() => {
                                handleOptionClick(category);
                              }}
                            >
                              <span className="mb-0">{category.name}</span>
                              <p className="mb-0">{category.path}</p>
                            </div>
                          ))
                        : searchText && (
                            <div className="category_option">
                              No matching options found.
                            </div>
                          )}
                    </div>
                  )}
                </div>
                <div className="spacer-single"></div>
                <h5>
                  Length of License
                  <span className="error-message-color">*</span>
                </h5>
                <MultipleSelect
                  value={selectedTerm}
                  options={licenseTerm}
                  handleSelect={onTermSelect}
                />
                <div className="spacer-single"></div>
                <h5>Description of Art available for Licensing</h5>
                <textarea
                  onChange={onChange}
                  value={artDetail?.description}
                  type="text"
                  name="description"
                  id="item_name"
                  maxLength={2500}
                  rows={10}
                  className="form-control"
                  placeholder="Describe the art that is available to a potential licensee for this product category"
                />
                <div className="limit">
                  <span className="char_limit">
                    {artDetail ? artDetail?.description?.length : 0}
                    /2500 characters
                  </span>
                </div>
                <div className="spacer-single"></div>
                <h5>
                  License Options<span className="error-message-color">*</span>
                </h5>
                <SingleSelect
                  value={selectedLicense}
                  options={licenseType}
                  handleSelect={onLicenseSelect}
                />
                <div className="spacer-single"></div>
                <h5>
                  Territory<span className="error-message-color">*</span>
                </h5>
                <MultipleSelect
                  value={selectedTerritory}
                  options={licenseTerritory}
                  handleSelect={onTerritorySelect}
                />
                <div className="spacer-single"></div>
                <div className="spacer-single"></div>
                {createArt.isLoading ? (
                  <Loading loadingMessage="Creating your License" />
                ) : (
                  <input
                    type="submit"
                    id="submit"
                    className="btn-main m-2"
                    value="List and Agree to Terms"
                    disabled={createArt.isLoading}
                  />
                )}
              </div>
            </form>
          </div>
          {window.innerWidth > 992 ? (
            <div className="col-lg-3 col-sm-6 col-xs-12">
              <h5
                style={{
                  top: "100px",
                  textAlign: "center",
                }}
              >
                Preview item
              </h5>
              <ArtCard
                style={{
                  top: "130px",
                }}
                className="d-item"
                name={artDetail?.name}
                previewImage={image}
              />
            </div>
          ) : (
            <div className="col-lg-3 col-sm-6 col-xs-12">
              <h5
                style={{
                  textAlign: "center",
                }}
              >
                Preview item
              </h5>
              <ArtCard
                className="d-item"
                name={artDetail?.name}
                previewImage={image}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CreateArt;
